import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type WhatsAppOperationMode = 'ai' | 'hybrid' | 'manual';
export type WhatsAppDiagStatus = 'ok' | 'warn' | 'error' | 'info' | 'skip';

/** Configuração devolvida pela API (NUNCA contém token nem App Secret em claro). */
export interface WhatsAppSettings {
  Id: number | null;
  IsEnabled: number;
  GraphApiVersion: string;
  AppId: string;
  PhoneNumberId: string;
  WabaId: string;
  VerifyToken: string;
  WebhookBaseUrl: string;
  DisplayPhoneNumber?: string | null;
  VerifiedName?: string | null;
  QualityRating?: string | null;
  OperationMode: WhatsAppOperationMode;
  AiModel?: string;
  AiFailoverEnabled?: boolean;
  AiSystemPrompt?: string;
  AllowUnknownNumbers: number;
  RequireAllowWhatsApp: number;
  MaxHistoryMessages: number;
  HandoffKeywords: string;
  HandoffMessage: string;
  NotAuthorizedMessage: string;
  UnsupportedMediaMessage: string;
  LastWebhookAt?: string | null;
  LastTestAt?: string | null;
  LastTestStatus?: string | null;
  LastTestMessage?: string | null;
  HasAccessToken: boolean;
  AccessTokenMasked: string;
  AccessTokenUnreadable?: boolean;
  HasAppSecret: boolean;
  AppSecretMasked: string;
  WebhookUrl: string;
  RawBodyWired?: boolean;
  IsConfigured: boolean;
}

/** Payload de gravação: segredos em branco mantêm o valor existente. */
export interface WhatsAppSettingsPayload extends Partial<
  Omit<WhatsAppSettings, 'IsEnabled' | 'AllowUnknownNumbers' | 'RequireAllowWhatsApp'>
> {
  IsEnabled: boolean;
  AllowUnknownNumbers: boolean;
  RequireAllowWhatsApp: boolean;
  AccessToken?: string;
  AppSecret?: string;
  RegenerateVerifyToken?: boolean;
}

export interface WhatsAppDiagItem {
  key: string;
  title: string;
  status: WhatsAppDiagStatus;
  detail: string;
  hint: string;
}

export interface WhatsAppDiagnostics {
  overall: 'ok' | 'warn' | 'error';
  canSend: boolean;
  canReceive: boolean;
  items: WhatsAppDiagItem[];
  checkedAt: string;
}

export interface WhatsAppMessageRow {
  Id: number;
  Wamid: string | null;
  Direction: 'in' | 'out';
  WaId: string;
  NIF: string | null;
  Body: string | null;
  MessageType: string;
  Status: string;
  ErrorCode: string | null;
  ErrorMessage: string | null;
  CreatedAt: string;
}

export interface WhatsAppEventRow {
  Id: number;
  EventType: string;
  IsSignatureValid: number | null;
  Summary: string | null;
  CreatedAt: string;
}

export interface WhatsAppApiError {
  message: string;
  hint?: string;
  metaCode?: number;
  schemaMissing?: boolean;
}

export interface WhatsAppTemplate {
  name: string;
  language: string;
  status: string;
  category?: string;
}

export interface WhatsAppPublicStatus {
  success: boolean;
  isAvailable: boolean;
  displayPhoneNumber: string | null;
}

/**
 * Serviço da parametrização do canal WhatsApp Business (Meta Cloud API) do Ricardo.
 * Endpoints: `${environment.URL}/whatsapp/*` (CRM ADM, apenas administradores).
 */
@Injectable({ providedIn: 'root' })
export class WhatsappSettingsService {
  private readonly API_URL = `${environment.URL}/whatsapp`;

  constructor(private http: HttpClient) {}

  private publicStatusCache$: Observable<WhatsAppPublicStatus> | null = null;
  private lastStatusFetch = 0;

  /**
   * Devolve o estado público do canal WhatsApp (se está ativo e configurado).
   * Totalmente seguro: NUNCA lança erro nem bloqueia o site (fallback { isAvailable: false }).
   * Inclui cache de 60 segundos com shareReplay para zero overhead de rede.
   */
  getPublicStatus(): Observable<WhatsAppPublicStatus> {
    const now = Date.now();
    if (this.publicStatusCache$ && now - this.lastStatusFetch < 60000) {
      return this.publicStatusCache$;
    }
    this.lastStatusFetch = now;
    this.publicStatusCache$ = this.http.get<WhatsAppPublicStatus>(`${this.API_URL}/public-status`).pipe(
      catchError(() => of({ success: true, isAvailable: false, displayPhoneNumber: null })),
      shareReplay(1),
    );
    return this.publicStatusCache$;
  }

  /** Invalida a cache de estado público (chamado após gravar definições no CRM). */
  clearPublicStatusCache(): void {
    this.publicStatusCache$ = null;
    this.lastStatusFetch = 0;
  }

  getSettings(): Observable<{ success: boolean; data: WhatsAppSettings }> {
    return this.http
      .get<{ success: boolean; data: WhatsAppSettings }>(`${this.API_URL}/settings`)
      .pipe(catchError(err => throwError(() => this.toApiError(err))));
  }

  saveSettings(
    payload: WhatsAppSettingsPayload,
  ): Observable<{ success: boolean; message: string; data: WhatsAppSettings }> {
    return this.http
      .post<{ success: boolean; message: string; data: WhatsAppSettings }>(`${this.API_URL}/settings`, payload)
      .pipe(catchError(err => throwError(() => this.toApiError(err))));
  }

  testConnection(): Observable<{ success: boolean; data: WhatsAppDiagnostics }> {
    return this.http
      .post<{ success: boolean; data: WhatsAppDiagnostics }>(`${this.API_URL}/test-connection`, {})
      .pipe(catchError(err => throwError(() => this.toApiError(err))));
  }

  testSend(payload: {
    to: string;
    mode: 'template' | 'text';
    templateName?: string;
    languageCode?: string;
    text?: string;
  }): Observable<{ success: boolean; message: string }> {
    return this.http
      .post<{ success: boolean; message: string }>(`${this.API_URL}/test-send`, payload)
      .pipe(catchError(err => throwError(() => this.toApiError(err))));
  }

  subscribe(): Observable<{ success: boolean; message: string }> {
    return this.http
      .post<{ success: boolean; message: string }>(`${this.API_URL}/subscribe`, {})
      .pipe(catchError(err => throwError(() => this.toApiError(err))));
  }

  registerPhone(pin: string): Observable<{ success: boolean; message: string }> {
    return this.http
      .post<{ success: boolean; message: string }>(`${this.API_URL}/register`, { pin })
      .pipe(catchError(err => throwError(() => this.toApiError(err))));
  }

  getTemplates(): Observable<{ success: boolean; data: WhatsAppTemplate[] }> {
    return this.http
      .get<{ success: boolean; data: WhatsAppTemplate[] }>(`${this.API_URL}/templates`)
      .pipe(catchError(err => throwError(() => this.toApiError(err))));
  }

  getMessages(limit = 30): Observable<{ success: boolean; data: WhatsAppMessageRow[] }> {
    return this.http
      .get<{ success: boolean; data: WhatsAppMessageRow[] }>(`${this.API_URL}/messages`, { params: { limit } })
      .pipe(catchError(err => throwError(() => this.toApiError(err))));
  }

  getEvents(limit = 30): Observable<{ success: boolean; data: WhatsAppEventRow[] }> {
    return this.http
      .get<{ success: boolean; data: WhatsAppEventRow[] }>(`${this.API_URL}/events`, { params: { limit } })
      .pipe(catchError(err => throwError(() => this.toApiError(err))));
  }

  /** Normaliza o erro HTTP preservando a explicação (hint) devolvida pela API. */
  private toApiError(error: HttpErrorResponse): WhatsAppApiError {
    const body = error?.error || {};
    return {
      message: body.message || error?.message || 'Erro inesperado ao contactar o servidor.',
      hint: body.hint,
      metaCode: body.metaCode,
      schemaMissing: !!body.schemaMissing,
    };
  }
}
